"use client";

import { FormInputCheckbox, FormInputDropdown, FormInputText } from '@/components/form';
import { useContext, useEffect, useRef, useState } from 'react';
import styles from './_hcp-sign-up.module.scss';
import Button from '@/components/button';
import Modal from '@/components/modal';
import PageAnchor from '@/components/page-anchor';
import ApiSignUp from '@/api-client/sign-up';
import Captcha from '@/components/captcha';
import { LayoutContext } from '@/components/layout-context-container';

export const hcpFullSpecialtyDropdownItems = [
  {
    label: <>Adult endocrinologist</>,
    labelText: 'Adult endocrinologist',
    code: 'ADE',
  }, {
    label: <>Pediatric endocrinologist</>,
    labelText: 'Pediatric endocrinologist',
    code: 'PDE',
  }, {
    label: <>Reproductive endocrinologist</>,
    labelText: 'Reproductive endocrinologist',
    code: 'RDE',
  }, {
    label: <>Urologist</>,
    labelText: 'Urologist',
    code: 'URO',
  }, {
    label: <>Genetic counselor</>,
    labelText: 'Genetic counselor',
    code: 'GNC',
  }, {
    label: <>Mental health care provider</>,
    labelText: 'Mental health care provider',
    code: 'MHCP',
  }, {
    label: <>Pediatrician</>,
    labelText: 'Pediatrician',
    code: 'PED',
  }, {
    label: <>Primary care physician</>,
    labelText: 'Primary care physician',
    code: 'PCPH',
  }, {
    label: <>Nurse practitioner</>,
    labelText: 'Nurse practitioner',
    code: 'NRP',
  }, {
    label: <>Physician assistant</>,
    labelText: 'Physician assistant',
    code: 'PHA',
  }, {
    label: <>Other</>,
    labelText: 'Other',
    code: 'OTH',
  }, 
];

export default function HcpSupportAndResourcesSignup() {
  const { setAutoScrollActive } = useContext(LayoutContext);
  const modalRef = useRef(null);
  const formFirstNameRef = useRef(null);
  const formLastNameRef = useRef(null);
  const formEmailRef = useRef(null);
  const formSpecialityRef = useRef(null);
  const formZipRef = useRef(null);
  const formNpiNumberRef = useRef(null);
  const formPhoneNumberRef = useRef(null);
  const formScheduleRef = useRef(null);
  const formInformedRef = useRef(null);
  const formAgreementRef = useRef(null);

  const [optinInvalid, setOptinInvalid] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [requiredNpi, setRequiredNpi] = useState(true);

  const [captchaInvalid, setCaptchaInvalid] = useState(false);
  const { getCaptchaToken, CaptchaDisclaimer, CaptchaErrorMessage } = Captcha();

  const resetForm = () => {
    formFirstNameRef.current?.reset();
    formLastNameRef.current?.reset();
    formEmailRef.current?.reset();
    formSpecialityRef.current?.reset();
    formZipRef.current?.reset();
    formNpiNumberRef.current?.reset();
    formPhoneNumberRef.current?.reset();
    formScheduleRef.current?.reset();
    formInformedRef.current?.reset();
    formAgreementRef.current?.reset();

    setSubmittingForm(false);
  };

  const validForm = async () => {
    let valid = true;

    const validateElements = [
      formFirstNameRef,
      formLastNameRef,
      formEmailRef,
      formZipRef,
      formSpecialityRef,
      formNpiNumberRef,
      formPhoneNumberRef,
    ];

    // formNpiNumberRef.current?.setValid(npiApiLookupValid);
    // setOptinInvalid(!optinValid);

    // valid = (validateElements.filter((validation) => validation === false)).length === 0;

    const formValid = validateElements.map(({current}) => {
      if (current) {
        return current.validate();
      }
      return false;
    });

    let hasError = false;

    const allValid = formValid.every((v, index) => {
      if (!v) {
        hasError = true;
        setAutoScrollActive(true);
        validateElements[index].current?.setFocus && validateElements[index].current.setFocus();
      }
      return v;
    });

    const optinValid = formScheduleRef.current?.checked || formInformedRef.current?.checked;
    const npiApiLookupValid = (formSpecialityRef.current?.code === 'OTH' && formNpiNumberRef.current?.value?.trim().length === 0) || !allValid
      ? true
      : await formNpiNumberRef.current?.apiValidation({
        url: '/api/validate-npi',
        method: 'POST',
        bodyPropName: 'npi',
        callback: (data) => {
          // api validation func
          // formNpiNumberRef.current?.setFocus && formNpiNumberRef.current.setFocus();
          const valid = data.result_count !== 0 && data.results?.length > 0;
          return valid;
        }
      });

    setOptinInvalid(!optinValid);

    if (allValid && (!npiApiLookupValid || !optinValid)) {

      if (!npiApiLookupValid && !hasError) {
        setAutoScrollActive(true);
        formNpiNumberRef.current?.setValid(npiApiLookupValid);
        formNpiNumberRef.current?.setFocus && formNpiNumberRef.current.setFocus();
      } else if (!optinValid && !hasError) {
        setAutoScrollActive(true);
        formScheduleRef.current?.setFocus && formScheduleRef.current.setFocus();
      }

      return false;
    } else if (!allValid) {
      valid = false;
    }

    if (!formAgreementRef.current?.validate()) {
      allValid && npiApiLookupValid && optinValid && setAutoScrollActive(true);
      allValid && npiApiLookupValid && optinValid && formAgreementRef.current?.setFocus && formAgreementRef.current.setFocus();
      valid = false;
    }

    return valid;
  }

  function handleOptinBlur() {
    const optinValid = formScheduleRef.current?.checked || formInformedRef.current?.checked;

    if (!optinValid) {
      setOptinInvalid(true);
    }
  }

  useEffect(() => {
    formScheduleRef.current?.setValid(!optinInvalid);
    formInformedRef.current?.setValid(!optinInvalid);

  }, [optinInvalid]);

  const handleSubmit = async () => {
    if (submittingForm || !await validForm()) return;

    setSubmittingForm(true);
    setCaptchaInvalid(false);

    const formData = {
      [formFirstNameRef.current?.name]: formFirstNameRef.current?.value,
      [formLastNameRef.current?.name]: formLastNameRef.current?.value,
      [formEmailRef.current?.name]: formEmailRef.current?.value,
      'answer_text': formSpecialityRef.current?.optionSelected,
      'answer_code': formSpecialityRef.current?.code,
      [formPhoneNumberRef.current?.name]: formPhoneNumberRef.current?.value,
      [formZipRef.current?.name]: formZipRef.current?.value,
      [formNpiNumberRef.current?.name]: formNpiNumberRef.current?.value,
      [formScheduleRef.current?.name]: formScheduleRef.current?.checked,
      [formInformedRef.current?.name]: formInformedRef.current?.checked,
      [formAgreementRef.current?.name]: formAgreementRef.current?.checked,
      isNowAvailable: true,
    };

    //console.log("Form Data:", formData);
    
    let successful = false;

    try {
      // reCAPTCHA
      const captchaToken = await getCaptchaToken();

      const {success, error} = await ApiSignUp({
        formId: 'hcp_signup',
        formData,
        captchaToken,
      });

      successful = success;

      if (error) {
        if (error?.type === 'CAPTCHA_VALIDATION' || error?.type === 'CAPTCHA_LOAD_ERROR') {
          setCaptchaInvalid(true);
        }
      }

      // console.log(success, errorMessage);
    } catch (error) {
      // console.error('Error submitting form:', error);

      if (error?.type === 'CAPTCHA_VALIDATION' || error?.type === 'CAPTCHA_LOAD_ERROR') {
        setCaptchaInvalid(true);
      }
    }

    setTimeout(() => {
      if (successful) {
        // Form was submitted
        modalRef.current?.open();
        resetForm();
      } else {
        console.log('Patient signup form error');
      }
  
      setSubmittingForm(false);
     
    }, 2000);
  }
  
  return (
    <>
      <PageAnchor anchorId="crm-sign-up" />
      <div className={styles.container}>
        <section>
          <div className="container">
            <h2 className="h1 page-heading">Sign up to learn more about CRENESSITY</h2>
            <p className={styles.subheading}>Receive more information about CRENESSITY or schedule a call/visit with a Neurocrine representative.</p>
            <p className={styles.required}>*Indicates required field</p>
            <div className={styles.form}>
              <div className={styles.form_row}>
                <FormInputText
                  ref={formFirstNameRef}
                  name="first_name"
                  label="First name"
                  placeholder="Enter first name"
                  placeholderErrorMessage="Please enter your first name."
                  errorMessage={null}
                  disabled={submittingForm}
                  maxLength={40}
                  required
                />
                <FormInputText
                  ref={formLastNameRef}
                  name="last_name"
                  label="Last name"
                  placeholder="Enter last name"
                  placeholderErrorMessage="Please enter your last name."
                  disabled={submittingForm}
                  maxLength={40}
                  required
                  errorMessage={null}
                />
              </div>
              <div className={styles.form_row}>
                <FormInputText
                  ref={formEmailRef}
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Enter email address"
                  placeholderErrorMessage="Please enter a valid email address."
                  disabled={submittingForm}
                  maxLength={254}
                  required
                  errorMessage={null}
                />
                <FormInputText
                  ref={formZipRef}
                  name="zip"
                  label="ZIP code"
                  type="zip"
                  maxLength={10}
                  placeholder="Enter ZIP code"
                  placeholderErrorMessage="Please enter your ZIP code."
                  disabled={submittingForm}
                  required
                  errorMessage={null}
                />
              </div>
              <div className={styles.form_row}>
                <FormInputDropdown
                  ref={formSpecialityRef}
                  name="specialty"
                  label="Which best describes your specialty?"
                  placeholder="I am..."
                  placeholderErrorMessage="Please enter your specialty."
                  disabled={submittingForm}
                  required
                  itemList={hcpFullSpecialtyDropdownItems}
                  omitFirstItem
                  disableFirstItem={false}
                  errorMessage={null}
                  onChange={({optionSelected}) => {
                    setRequiredNpi(optionSelected !== 'Other');

                    if (!formNpiNumberRef.current.valid && optionSelected === 'Other') {
                      formNpiNumberRef.current?.setValid(true);
                    }
                  }}
                />
                <FormInputText
                  ref={formNpiNumberRef}
                  name="npi_id"
                  label="NPI number"
                  placeholder="Enter NPI number"
                  autoformatNumericOnly
                  equalMaxLengthValidation
                  maxLength={10}
                  disabled={submittingForm}
                  placeholderErrorMessage="Please enter valid NPI number."
                  errorMessage={null}
                  required={requiredNpi}
                />
              </div>
              <div className={`${styles.form_row} !mb-4 ${styles.form_row_invert}`}>
                <div className={styles.contact}>
                <span className={styles.contact_invalid}>Please select one or more to sign up.</span>
                <p className="mb-2"><strong>*I would like to: (select one or more)</strong></p>
                  <FormInputCheckbox
                    ref={formScheduleRef}
                    name="schedule_visit"
                    customClass="mb-2 form-input-checkbox"
                    disabled={submittingForm}
                    onClick={() => {
                      if (!formScheduleRef.current?.checked && !formInformedRef.current?.checked) {
                        formScheduleRef.current?.setValid(false);
                        setOptinInvalid(true);
                      } else {
                        setOptinInvalid(false);
                      }
                    }}
                    errorMessage={null}
                    omitBlurValidate
                    data-gtm-event-category="Form"
                    data-gtm-event-action="Click"
                    data-gtm-event-label="Schedule a call or a visit with a representative"
                  >
                    Schedule a call or visit with a representative
                  </FormInputCheckbox>
                  <FormInputCheckbox
                    ref={formInformedRef}
                    name="stay_informed"
                    disabled={submittingForm}
                    customClass="mb-2 form-input-checkbox"
                    onClick={() => setOptinInvalid(false)}
                    onBlur={handleOptinBlur}
                    errorMessage={null}
                    omitBlurValidate
                    data-gtm-event-category="Form"
                    data-gtm-event-action="Click"
                    data-gtm-event-label="Stay informed with updates and resources"
                  >
                    Stay informed with updates and resources
                  </FormInputCheckbox>        
                </div>
                <FormInputText
                  ref={formPhoneNumberRef}
                  name="phone_number"
                  label="Phone number"
                  type="tel"
                  placeholder="Enter phone number"
                  errorMessage={null}
                  disabled={submittingForm}
                  maxLength={14}
                />
              </div>
              <div className={styles.form_row}>
                <div className="hidden mobile:block" />
                <div className={styles.agreement}>
                  <span className={styles.agreement_invalid}>You must agree and check the box to sign up.</span>
                  <p className="mb-2"><strong>Required</strong></p>
                  <FormInputCheckbox
                    ref={formAgreementRef}
                    name="agreement"
                    disabled={submittingForm}
                    errorMessage={null}
                    required
                    forceShowAsterisc
                    data-gtm-event-category="Form"
                    data-gtm-event-action="Click"
                    data-gtm-event-label="Agree to Privacy Policy and Terms"
                  > 
                    You are confirming that you understand and agree to the <Button
                      className="font-bold underline text-hcp-grape"
                      href="https://www.neurocrine.com/privacy-policy/"
                      targetBlank
                      data-gtm-event-category="Form"
                      data-gtm-event-action="Click"
                      data-gtm-event-label="Privacy Policy"
                    >Privacy Policy</Button> and <Button
                      className="font-bold underline text-hcp-grape"
                      href="https://www.neurocrine.com/terms-conditions/"
                      targetBlank
                      data-gtm-event-category="Form"
                      data-gtm-event-action="Click"
                      data-gtm-event-label="Terms and Conditions"
                    >Terms and Conditions</Button>. 
                  </FormInputCheckbox>
                </div>
              </div>
              <CaptchaErrorMessage customClass="!mt-6" invalid={captchaInvalid} />
              <Button
                type="button"
                variant={['outline--eggplant', 'hcp-big']}
                iconType={submittingForm ? 'loading-eggplant' : undefined}
                customClass={styles.submit_button}
                onClick={handleSubmit}
                disabled={submittingForm}
                data-gtm-event-category="Form"
                data-gtm-event-action="Click"
                data-gtm-event-label="Sign up"
              >
                Sign up
              </Button>
              <CaptchaDisclaimer />
            </div>
          </div>
        </section>
        <Modal ref={modalRef}>
          <p className={`h1 ${styles.modal_heading}`}>Thank you!</p>
          <p className={styles.modal_body}>There&apos;s much more to come—we&apos;ll be in touch&nbsp;shortly.</p>
        </Modal>
      </div>
    </>
  );
}
